import { DEFAULT_INTEREST_TAGS } from '../constants/interests';
import { getCachedData, setCachedData } from '../utils/cache';

const CACHE_KEY = 'interest_tags';
const DEMO_OPTIONS_CACHE_KEY = 'demographic_options';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get interest tags with cache + fallback strategy
 * 1. Check cache (24h TTL)
 * 2. Fetch from API if cache miss
 * 3. Fallback to default constants if API fails
 */
export async function getInterestTags(): Promise<string[]> {
  // Check cache first
  const cached = getCachedData<string[]>(CACHE_KEY, CACHE_TTL);
  if (cached) {
    return cached;
  }

  // Fetch from API - use /api/marketing/ prefix so Cloudflare Pages middleware
  // routes it correctly (rewrites to /api/v1/ on backend)
  try {
    const response = await fetch('/api/marketing/library/interest-tags');
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    const tags = data.tags as string[];

    // Cache for next time
    setCachedData(CACHE_KEY, tags);
    
    return tags;
  } catch (error) {
    console.warn('Failed to fetch interest tags, using defaults:', error);
    return [...DEFAULT_INTEREST_TAGS];
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Demographic Options (for DemographicsForm selects)
// ─────────────────────────────────────────────────────────────────────────────

export interface DemographicOption {
  value: string;
  label: string;
  description?: string;
}

export interface DemographicOptions {
  tone: DemographicOption[];
  emoji_usage: DemographicOption[];
  response_length: DemographicOption[];
  vocabulary: DemographicOption[];
  forms_of_address: DemographicOption[];
}

/** Fallback options used when API is unavailable */
const DEFAULT_DEMOGRAPHIC_OPTIONS: DemographicOptions = {
  tone: [
    { value: 'casual', label: 'Đời thường', description: 'Giọng điệu đời thường, như cách trò chuyện xã hội' },
    { value: 'professional', label: 'Chuyên nghiệp', description: 'Giọng văn chuyên nghiệp, ngữ pháp rõ ràng, xưng hô lịch sự' },
    { value: 'enthusiastic', label: 'Phấn khích', description: 'Giọng điệu phấn khích, hưởng ứng tích cực' },
    { value: 'curious', label: 'Tò mò', description: 'Giọng điệu tò mò, hay đào sâu câu chuyện' },
  ],
  emoji_usage: [
    { value: 'none', label: 'Không dùng', description: 'Không sử dụng Emoji' },
    { value: 'rare', label: 'Hiếm khi', description: 'Hiếm khi sử dụng Emoji' },
    { value: 'moderate', label: 'Vừa phải', description: 'Sử dụng emoji vừa phải' },
    { value: 'frequent', label: 'Thường xuyên', description: 'Thường xuyên sử dụng emoji' },
  ],
  response_length: [
    { value: 'short', label: 'Ngắn gọn', description: 'Tối đa 10 chữ' },
    { value: 'medium', label: 'Vừa phải', description: 'Từ 11 đến 30 chữ' },
    { value: 'long', label: 'Chi tiết', description: 'Tối thiểu 31 chữ trở lên' },
  ],
  vocabulary: [
    { value: 'simple', label: 'Đơn giản', description: 'Từ ngữ đơn giản, đơn điệu' },
    { value: 'casual', label: 'Đời thường', description: 'Từ ngữ đời thường, giản dị, gần gũi, hay sử dụng teencode' },
    { value: 'professional', label: 'Chuyên nghiệp', description: 'Từ ngữ chuyên nghiệp, cú pháp rõ ràng, ít teencode' },
    { value: 'technical', label: 'Học thuật', description: 'Thuật ngữ chuyên ngành, từ ngữ học thuật uyên bác' },
  ],
  forms_of_address: [
    { value: 'friendly', label: 'Thân thiện', description: 'Thân thiện, gần gũi' },
    { value: 'sibling', label: 'Quy mến', description: 'Quy mến, thân thiết' },
    { value: 'gen_z', label: 'Trẻ trung GenZ', description: 'Trẻ trung, GenZ' },
    { value: 'professional', label: 'Chuyên nghiệp', description: 'Chuyên nghiệp, tôn trọng' },
  ],
};

/**
 * Get conversation style options with cache + fallback strategy
 * Pattern mirrors getInterestTags(): cache 24h, fallback to DEFAULT_DEMOGRAPHIC_OPTIONS
 */
export async function getDemographicOptions(): Promise<DemographicOptions> {
  const cached = getCachedData<DemographicOptions>(DEMO_OPTIONS_CACHE_KEY, CACHE_TTL);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch('/api/marketing/library/demographic-options');

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    const options = data.options as DemographicOptions;

    setCachedData(DEMO_OPTIONS_CACHE_KEY, options);
    return options;
  } catch (error) {
    console.warn('Failed to fetch demographic options, using defaults:', error);
    return { ...DEFAULT_DEMOGRAPHIC_OPTIONS };
  }
}
